import { Box, Button, Card, CardContent, Chip, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { useState } from "react";

import {
  BulkActionBar,
  BulkButton,
  BulkCheckbox,
  useBulkSelection,
} from "../../components/bulk-selection/index.js";
import { PageHeader } from "../../components/PageHeader.js";
import { BulkDeleteDialog } from "../../components/ui/bulk-delete-dialog.js";
import { ConfirmDialog } from "../../components/ui/confirm-dialog.js";
import { BackButton } from "@/components/ui/back-button";
import { PageLoader } from "@/components/ui/page-loader";
import {
  bulkDeleteSolutionAreas,
  deleteSolutionArea,
  listSolutionAreas,
} from "./api.js";
import { EditSolutionAreaDialog } from "./EditSolutionAreaDialog.js";
import { NewSolutionAreaModal } from "./NewSolutionAreaModal.js";

/**
 * Solution-area (product-line pillar) management — the dashboard home for the
 * "Solution" switcher axis, reached from the editor-only "Manage" link on the
 * switcher. Lists every pillar (visible + hidden) with its tagged-doc count and
 * lets editors add / rename / hide / delete them.
 *
 * Unlike a CATEGORY delete (which can cascade-purge the docs inside the
 * folder), deleting a solution area is non-destructive to documents: the pillar
 * + its join rows go, and every tagged doc is simply UNTAGGED. So there's no
 * "destroy vs keep documents" picker here — the dialogs just spell out that the
 * docs survive. Selection uses the shared `useBulkSelection` module, same as
 * the categories page.
 */
export function SolutionAreasManagePage(): JSX.Element {
  const qc = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["docs", "solution-areas"],
    queryFn: listSolutionAreas,
  });

  const rows = data?.data ?? [];
  const bulk = useBulkSelection(rows);

  const [showCreate, setShowCreate] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    id: number;
    name: string;
    document_count: number;
  } | null>(null);
  const [pendingEdit, setPendingEdit] = useState<{
    id: number;
    name: string;
    visible: boolean;
    visibleToResellers: boolean;
  } | null>(null);
  const [confirmBulk, setConfirmBulk] = useState(false);

  const invalidate = (): Promise<void> =>
    qc.invalidateQueries({ queryKey: ["docs"] }).then(() => undefined);

  const remove = useMutation({
    mutationFn: (id: number) => deleteSolutionArea(id),
    onSuccess: () => void invalidate(),
  });

  const bulkRemove = useMutation({
    mutationFn: (ids: number[]) => bulkDeleteSolutionAreas(ids),
    onSuccess: () => {
      bulk.clear();
      setConfirmBulk(false);
      void invalidate();
    },
    onError: (err) => {
      console.error("[SolutionAreasManagePage] bulk delete failed", err);
    },
  });

  const selectedRows = rows.filter((r) => bulk.isSelected(r.id));

  return (
    <Box sx={{ display: "grid", gap: 3 }}>
      <PageHeader
        title="Manage solution areas"
        subtitle="Product-line pillars for the “Solution” switcher. A document can be tagged with several. Deleting a pillar only untags its documents — it never deletes them."
        right={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BackButton to="/documentation" label="Back to documents" />
            <Button variant="contained" onClick={() => setShowCreate(true)}>
              + New solution area
            </Button>
          </Box>
        }
      />

      <BulkActionBar count={bulk.count} onClear={bulk.clear}>
        <BulkButton
          variant="destructive"
          disabled={bulkRemove.isPending}
          onClick={() => setConfirmBulk(true)}
        >
          <DeleteOutlineIcon sx={{ fontSize: 14 }} />
          Delete {bulk.count} {bulk.count === 1 ? "solution area" : "solution areas"}
        </BulkButton>
      </BulkActionBar>

      {(remove.isError || bulkRemove.isError) && (
        <Box
          role="alert"
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: "error.main",
            bgcolor: (t) => alpha(t.palette.error.main, 0.08),
            px: 2,
            py: 1.5,
            fontSize: 14,
            color: "error.main",
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
            {bulkRemove.isError ? "Bulk delete failed" : "Delete failed"}
          </Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.8 }}>
            {(bulkRemove.error ?? remove.error) instanceof Error
              ? (bulkRemove.error ?? remove.error)!.message
              : "Unknown error. Check the browser console + dashboard server logs."}
          </Typography>
        </Box>
      )}

      {isLoading && <PageLoader />}

      {isError && (
        <Card>
          <CardContent sx={{ display: "grid", gap: 1, py: 4, textAlign: "center" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "error.main" }}>
              Couldn't load solution areas.
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
              {error instanceof Error ? error.message : "Unknown error"}
            </Typography>
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <Card>
          <CardContent sx={{ display: "grid", gap: 1.5, py: 6, textAlign: "center" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
              No solution areas yet.
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
              Add a pillar (e.g. Unified Communications) so documents can be filed under it.
            </Typography>
            <Box>
              <Button variant="contained" size="small" onClick={() => setShowCreate(true)}>
                + New solution area
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <Card sx={{ overflow: "hidden" }}>
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, "& > li + li": { borderTop: 1, borderColor: "divider" } }}>
            <Box
              component="li"
              data-testid="solution-areas-select-all-row"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.4),
                px: 2.5,
                py: 1,
                fontSize: 12,
                color: "text.disabled",
              }}
            >
              <BulkCheckbox
                checked={bulk.allOnPageChecked}
                indeterminate={bulk.someOnPageChecked}
                onChange={(checked) => bulk.toggleAllOnPage(checked)}
                ariaLabel="Select all solution areas"
              />
              <Box component="span">{`Select all (${rows.length})`}</Box>
            </Box>
            {rows.map((s) => {
              const docCount = s.document_count ?? 0;
              return (
                <Box
                  component="li"
                  key={s.id}
                  data-testid={`solution-areas-row-${s.id}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    px: 2.5,
                    py: 1.5,
                    transition: "background-color 150ms",
                    "&:hover": { bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.5) },
                    "&:hover .doc-sa-controls": { opacity: 1 },
                  }}
                >
                  <BulkCheckbox
                    checked={bulk.isSelected(s.id)}
                    onChange={(checked, opts) => bulk.setChecked(s.id, checked, opts)}
                    ariaLabel={`Select solution area ${s.name}`}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ mb: 0.5, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                      <Typography
                        component="h3"
                        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14, fontWeight: 500, lineHeight: 1.25, color: "text.primary" }}
                      >
                        {s.name}
                      </Typography>
                      {s.visible_in_customer_portal === false && (
                        <Chip
                          label="Hidden"
                          size="small"
                          sx={{ height: 20, borderRadius: 0.75, fontSize: 11, bgcolor: "surfaceSoft", color: "text.secondary" }}
                        />
                      )}
                      {/* Positive chip, like the categories list: reseller
                          exposure is opt-in, so marking the exceptions is
                          what carries information. */}
                      {s.visible_in_reseller_portal === true && (
                        <Chip
                          label="Resellers"
                          size="small"
                          data-testid={`solution-areas-row-resellers-${s.id}`}
                          sx={{ height: 20, borderRadius: 0.75, fontSize: 11, bgcolor: "action.selected", color: "primary.main" }}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, fontSize: "11px", color: "text.disabled" }}>
                      <Box component="span">{s.slug}</Box>
                      <Box component="span">
                        {`· ${docCount.toLocaleString()} document${docCount === 1 ? "" : "s"}`}
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    className="doc-sa-controls"
                    sx={{ display: "flex", flexShrink: 0, alignItems: "center", gap: 0.5, opacity: 0.8, transition: "opacity 150ms" }}
                  >
                    <IconButton
                      size="small"
                      title={`Rename solution area ${s.name}`}
                      aria-label={`Edit solution area ${s.name}`}
                      data-testid={`solution-areas-row-edit-${s.id}`}
                      onClick={() =>
                        setPendingEdit({
                          id: s.id,
                          name: s.name,
                          visible: s.visible_in_customer_portal !== false,
                          visibleToResellers: s.visible_in_reseller_portal === true,
                        })
                      }
                      sx={{ color: "text.disabled" }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      title={`Delete solution area ${s.name}`}
                      aria-label={`Delete solution area ${s.name}`}
                      data-testid={`solution-areas-row-delete-${s.id}`}
                      onClick={() =>
                        setPendingDelete({
                          id: s.id,
                          name: s.name,
                          document_count: docCount,
                        })
                      }
                      sx={{ color: "error.main" }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Card>
      )}

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Delete solution area?"
        description={
          pendingDelete
            ? pendingDelete.document_count > 0
              ? `"${pendingDelete.name}" will be removed and its ${pendingDelete.document_count} tagged document${pendingDelete.document_count === 1 ? "" : "s"} untagged. The documents themselves are NOT deleted. This cannot be undone.`
              : `"${pendingDelete.name}" will be removed. This cannot be undone.`
            : ""
        }
        confirmLabel="Delete solution area"
        variant="destructive"
        onConfirm={async () => {
          if (!pendingDelete) return;
          await remove.mutateAsync(pendingDelete.id);
        }}
      />

      <BulkDeleteDialog
        isOpen={confirmBulk}
        onClose={() => setConfirmBulk(false)}
        items={selectedRows.map((s) => ({
          id: s.id,
          name: s.name,
          document_count: s.document_count ?? 0,
        }))}
        entityLabel={{ singular: "Solution area", plural: "Solution areas" }}
        cascadingWarning="These pillars will be removed and every tagged document untagged — the documents themselves are NOT deleted."
        renderItem={(it) => <>{it.name}</>}
        renderItemBadge={(it) =>
          it.document_count > 0 ? (
            <Box component="span" sx={{ fontSize: "10px", color: "text.disabled" }}>
              {it.document_count} doc{it.document_count === 1 ? "" : "s"} → untagged
            </Box>
          ) : null
        }
        onConfirm={async () => {
          await bulkRemove.mutateAsync([...bulk.selected]);
        }}
      />

      <NewSolutionAreaModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          setShowCreate(false);
          void invalidate();
        }}
      />

      <EditSolutionAreaDialog
        target={pendingEdit}
        onClose={() => setPendingEdit(null)}
        onSaved={() => {
          setPendingEdit(null);
          void invalidate();
        }}
      />
    </Box>
  );
}
